import { useQuery } from '@tanstack/react-query';
import {
  fetchMarketPricing,
  fetchFromJustTCGv2,
  JustTCGCardData,
} from '../services/justTcgService';

/**
 * Custom hook to fetch card pricing with TanStack Query.
 * Currently uses OnePiece pricing service which is compatible with the backend generic pricing.
 * Caches results for 6 hours.
 */
export function useMarketPricing(
  cardNumber: string,
  cardName: string,
  setId: string,
  rarity?: string,
  enabled: boolean = true,
  searchQuery?: string, // Optional: Override the search query for JustTCG API
  justtcgId?: string, // Optional: Direct JustTCG ID
) {
  return useQuery<JustTCGCardData | null, Error>({
    queryKey: [
      'market-pricing',
      cardNumber,
      cardName,
      setId,
      rarity,
      searchQuery,
      justtcgId,
    ],
    queryFn: () =>
      fetchMarketPricing(cardNumber, cardName, setId, rarity, searchQuery, justtcgId),
    enabled: enabled && !!cardNumber && !!cardName && !!setId,
    staleTime: 6 * 60 * 60 * 1000, // 6 hours
    cacheTime: 6 * 60 * 60 * 1000, // 6 hours
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}

/**
 * Custom hook to fetch card pricing from JustTCG v2 (direct JustTCG Pokemon data).
 */
export function useJustTCGv2Pricing(
  cardNumber: string,
  cardName: string,
  setId: string,
  enabled: boolean = true,
  searchQuery?: string,
  justtcgId?: string,
  cache?: string,
) {
  return useQuery<JustTCGCardData | null, Error>({
    queryKey: [
      'justtcg-v2-pricing',
      cardNumber,
      cardName,
      setId,
      searchQuery,
      justtcgId,
      cache,
    ],
    queryFn: () =>
      fetchFromJustTCGv2(cardNumber, cardName, setId, searchQuery, justtcgId, cache),
    enabled: enabled && !!cardNumber && !!cardName && !!setId,
    staleTime: cache === 'false' ? 0 : 6 * 60 * 60 * 1000, // 0 if cache=false, otherwise 6 hours
    cacheTime: cache === 'false' ? 0 : 6 * 60 * 60 * 1000, // 0 if cache=false, otherwise 6 hours
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}

